<template>
  <IdentityShell>
    <div class="directory-results">
      <p class="kicker">
        {{
          $t(
            'auth.identity.directoryResults.pickAPlace',
            'Pick a place to land'
          )
        }}
      </p>

      <h1 class="section-headline directory-results__title">
        {{ $t('auth.identity.directoryResults.youreIn', "You're in. Pick an") }}
        <span class="highlight">{{
          $t(
            'auth.identity.directoryResults.organizationHighlight',
            'organization'
          )
        }}</span
        >{{ $t('auth.identity.directoryResults.organizationTrail', '.') }}
      </h1>
      <p class="section-lede directory-results__lede">
        {{
          $t(
            'auth.identity.directoryResults.ledeIntro',
            'These are the Communal organizations linked to'
          )
        }}
        <strong>{{ email }}</strong
        >{{
          $t(
            'auth.identity.directoryResults.ledeOutro',
            '. Forgot a password? Use the "Forgot password" link after you click through.'
          )
        }}
      </p>

      <p v-if="!tenants.length" class="directory-results__empty">
        {{
          $t(
            'auth.identity.directoryResults.noOrganizationsFound',
            "We couldn't find any active organizations for this email. Reach out to your admin if you think this is wrong."
          )
        }}
      </p>

      <ul v-else class="directory-results__list">
        <li
          v-for="tenant in pagedTenants"
          :key="tenant.url"
          class="directory-results__item"
        >
          <a :href="tenant.url" class="directory-results__link">
            <span class="directory-results__detail">
              <span class="directory-results__name">{{ tenant.name }}</span>
              <span class="directory-results__url">
                {{ displayHost(tenant.url) }}
              </span>
            </span>
            <IconArrowRight class="directory-results__chevron" />
          </a>
        </li>
      </ul>

      <div v-if="showPagination" class="directory-results__pagination">
        <p class="directory-results__pagination-summary" aria-live="polite">
          {{ $t('auth.identity.directoryResults.showing', 'Showing') }}
          <strong>{{ rangeStart }}</strong>
          –
          <strong>{{ rangeEnd }}</strong>
          {{ $t('auth.identity.directoryResults.of', 'of') }}
          <strong>{{ tenants.length }}</strong>
          {{
            $t('auth.identity.directoryResults.organizations', 'organizations')
          }}
        </p>
        <PaginationBase
          v-model="currentPage"
          size="sm"
          align="center"
          :total-rows="tenants.length"
          :per-page="perPage"
          :aria-label="
            $t(
              'auth.identity.directoryResults.organizationPages',
              'Organization pages'
            )
          "
        />
      </div>

      <p class="auth-foot-link directory-results__back">
        <RouterLink to="/login">
          {{
            $t(
              'auth.identity.directoryResults.lookUpAnotherEmail',
              'Look up another email'
            )
          }}
        </RouterLink>
      </p>
    </div>
  </IdentityShell>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { PaginationBase } from '@/components/base/Pagination';
import { IdentityShell } from '@/components/feature/Identity';
import { IconArrowRight } from '@/components/ui/Icon';
import { useIdentityBoot } from '@/composables/useIdentityBoot';

interface DiscoveryTenant {
  name: string;
  url: string;
}

const PER_PAGE = 10;

const { t } = useI18n({ useScope: 'global' });

const boot = useIdentityBoot();

const data = computed(
  () =>
    (boot.data ?? {}) as {
      email?: string;
      tenants?: DiscoveryTenant[];
    }
);

const email = computed(
  () =>
    data.value.email ??
    t('auth.identity.directoryResults.yourEmailFallback', 'your email')
);
const tenants = computed<DiscoveryTenant[]>(() => data.value.tenants ?? []);

const perPage = PER_PAGE;
const currentPage = ref(1);

const totalPages = computed(() =>
  Math.max(1, Math.ceil(tenants.value.length / perPage))
);

// If the underlying list shrinks (unlikely, but defensive), clamp the page
// back into range so we never render an empty paginated view.
watch(totalPages, (pages) => {
  if (currentPage.value > pages) {
    currentPage.value = pages;
  }
});

const showPagination = computed(() => tenants.value.length > perPage);

const pagedTenants = computed<DiscoveryTenant[]>(() => {
  if (!showPagination.value) {
    return tenants.value;
  }

  const start = (currentPage.value - 1) * perPage;

  return tenants.value.slice(start, start + perPage);
});

const rangeStart = computed(() =>
  tenants.value.length === 0 ? 0 : (currentPage.value - 1) * perPage + 1
);

const rangeEnd = computed(() =>
  Math.min(currentPage.value * perPage, tenants.value.length)
);

function displayHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
</script>

<style lang="scss" scoped>
// Rendered inside IdentityShell's .communal-surface scope — raw <h1>,
// .kicker, .section-headline, and .section-lede are deliberate
// marketing-surface classes, not in-product wrappers. See IdentityShell.vue
// for the full contract.

.directory-results__title {
  margin-bottom: 0.625rem;
}

.directory-results__lede {
  margin-bottom: 1.5rem;

  strong {
    color: var(--ink-900);
    font-weight: 600;
  }
}

.directory-results__empty {
  margin: 0 0 1rem;
  padding: 1.25rem;
  border: 1px dashed var(--rule);
  border-radius: 0.5rem;
  background: var(--surface-soft);
  color: var(--ink-600);
  text-align: center;
  font-size: 0.95rem;
}

.directory-results__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.directory-results__link {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--rule);
  background: var(--surface);
  color: var(--ink-900);
  text-decoration: none;
  transition:
    border-color 160ms var(--ease-decelerate),
    background 160ms var(--ease-decelerate),
    transform 160ms var(--ease-decelerate),
    box-shadow 160ms var(--ease-decelerate);

  &:hover,
  &:focus-visible {
    border-color: var(--communal-300);
    background: var(--surface);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px -10px rgba(20, 54, 89, 0.32);
    text-decoration: none;
    color: var(--ink-900);
  }

  &:hover .directory-results__chevron,
  &:focus-visible .directory-results__chevron {
    color: var(--communal-600);
    transform: translateX(2px);
  }
}

.directory-results__detail {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1;
  min-width: 0;
}

.directory-results__name {
  font-weight: 600;
  font-size: 1rem;
  color: var(--ink-900);
}

.directory-results__url {
  font-size: 0.8125rem;
  color: var(--ink-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.directory-results__chevron {
  width: 0.85rem;
  height: 0.85rem;
  color: var(--ink-500);
  transition:
    color 160ms var(--ease-decelerate),
    transform 160ms var(--ease-decelerate);
}

.directory-results__pagination {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px dashed var(--rule);
}

.directory-results__pagination-summary {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--ink-500);

  strong {
    color: var(--ink-900);
    font-weight: 600;
  }
}

// Quieter anchor than the shared .auth-foot-link treatment — this is a
// low-key escape hatch, not a call to action.
.directory-results__back a {
  color: var(--ink-500);
  font-weight: 400;
  border-bottom: 0;

  &:hover,
  &:focus-visible {
    color: var(--communal-600);
  }
}
</style>
